import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const MachineSupervisorContext = createContext(null);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

export function MachineSupervisorProvider({ children }) {
  const [machinesSupervisor, setMachinesSupervisor] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchAllocatedMachines = useCallback(async (supervisorId) => {
    if (!supervisorId) {
      setError('Supervisor ID is missing');
      setMachines([]);
      setMachinesSupervisor(null);
      return false;
    }

    setLoading(true);
    setError('');

    try {
      const endpoint = `${API_BASE_URL}/api/machinesupervisor/machines/${supervisorId}`;
      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`Failed to fetch machines. HTTP status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch allocated machines');
      }

      const allocatedMachines = Array.isArray(data.machines) ? data.machines : [];
      const machineCount = data.machineCount || allocatedMachines.length || 0;
      const activeMachines = data.activeMachines || 0;

      setMachines(allocatedMachines);
      setMachinesSupervisor({
        employeeId: supervisorId,
        machineCount,
        activeMachines,
        idleMachines: data.idleMachines ?? Math.max(machineCount - activeMachines, 0),
        groupBreakdown: data.groupBreakdown || {},
      });

      return true;
    } catch (err) {
      setMachines([]);
      setMachinesSupervisor(null);
      setError(err.message || 'Failed to fetch allocated machines');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearMachineSupervisorState = useCallback(() => {
    setMachinesSupervisor(null);
    setMachines([]);
    setError('');
  }, []);

  const value = useMemo(() => ({
    machinesSupervisor,
    setMachinesSupervisor,
    machines,
    setMachines,
    loading,
    error,
    setError,
    fetchAllocatedMachines,
    clearMachineSupervisorState,
  }), [
    machinesSupervisor,
    machines,
    loading,
    error,
    fetchAllocatedMachines,
    clearMachineSupervisorState,
  ]);

  return (
    <MachineSupervisorContext.Provider value={value}>
      {children}
    </MachineSupervisorContext.Provider>
  );
}

export function useMachineSupervisor() {
  const context = useContext(MachineSupervisorContext);

  if (!context) {
    throw new Error('useMachineSupervisor must be used within MachineSupervisorProvider');
  }

  return context;
}
